import { supabase } from '../lib/supabase';
import { Workspace, Project, UserRole, OnboardingQuestion } from '../types';

/**
 * Fetch all workspaces for the authenticated user
 */
export async function fetchUserWorkspaces(userId: string): Promise<Workspace[]> {
  const { data, error } = await supabase
    .from('workspace_members')
    .select(`
      workspace_id,
      role,
      workspaces (
        id,
        name,
        initials,
        role
      )
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching workspaces:', error);
    return [];
  }

  if (!data) return [];

  // Transform the data to match Workspace interface
  return data.map((member: any) => ({
    id: member.workspaces.id,
    name: member.workspaces.name,
    initials: member.workspaces.initials,
    role: (member.role === 'PROVIDER' ? UserRole.PROVIDER : UserRole.RECEIVER) as UserRole,
  }));
}

/**
 * Generate a unique workspace name with incrementing suffix if needed
 * Checks existing workspaces for the user to avoid duplicates
 * Handles edge cases like manually named "My Workspace 2" existing without "My Workspace"
 */
async function generateUniqueWorkspaceName(userId: string, baseName: string = 'My Workspace'): Promise<string> {
  const allWorkspaces = await fetchUserWorkspaces(userId);

  // Escape special regex characters in base name
  const escapedBaseName = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Pattern matches: baseName, baseName 1, baseName 2, etc.
  const pattern = new RegExp(`^${escapedBaseName}(\\s+(\\d+))?$`);
  const existingNumbers = new Set<number>();

  // Find all existing workspace names matching the pattern
  allWorkspaces.forEach(ws => {
    const match = ws.name.match(pattern);
    if (match) {
      if (match[2]) {
        // Has a number suffix (e.g., "My Workspace 2")
        existingNumbers.add(parseInt(match[2], 10));
      } else {
        // Matches base name exactly with no number (e.g., "My Workspace")
        // Treat as number 1 in our numbering scheme
        existingNumbers.add(1);
      }
    }
  });

  // If no matches found, base name is available
  if (existingNumbers.size === 0) {
    return baseName;
  }

  // Find the next available number (starting from 2, since 1 represents the base name)
  let nextNumber = 2;
  while (existingNumbers.has(nextNumber)) {
    nextNumber++;
  }

  return `${baseName} ${nextNumber}`;
}

/**
 * Create a new workspace and add the user as a member
 * If name is empty or not provided, generates a unique name automatically
 */
export async function createWorkspace(
  userId: string,
  name?: string,
  role: UserRole = UserRole.PROVIDER
): Promise<Workspace> {
  // Generate unique name if not provided or empty
  const workspaceName = (name || '').trim() || await generateUniqueWorkspaceName(userId);
  const initials = workspaceName.substring(0, 1).toUpperCase();
  const dbRole = role === UserRole.PROVIDER ? 'PROVIDER' : 'RECEIVER';

  // Use RPC to create workspace and member atomically to avoid RLS circular dependency
  const { data: workspace, error: workspaceError } = await supabase
    .rpc('create_workspace_with_member', {
      p_name: workspaceName,
      p_initials: initials,
      p_role: dbRole,
      p_user_id: userId
    });

  if (workspaceError || !workspace) {
    console.error('Error creating workspace via RPC:', workspaceError);
    throw new Error(workspaceError?.message || 'Failed to create workspace. Please try again.');
  }

  return {
    id: workspace.id,
    name: workspace.name,
    initials: workspace.initials,
    role: (workspace.role === 'PROVIDER' ? UserRole.PROVIDER : UserRole.RECEIVER) as UserRole,
  };
}

/**
 * Fetch all projects for a specific workspace
 * Enforces workspace isolation by accepting only a single workspace ID
 * Note: RLS policies ensure users only see projects in their workspaces
 */
export async function fetchWorkspaceProjects(workspaceId: string): Promise<Project[]> {
  if (!workspaceId) return [];

  // Fetch all projects in the specific workspace - RLS policies ensure user only sees authorized projects
  let query = supabase
    .from('projects')
    .select(
      `
      *,
      milestones (
        *,
        comments (*)
      ),
      notes (*)
    `
    )
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching projects:', error);
    return [];
  }

  if (!data) return [];

  return data.map((project: any) => ({
    id: project.id,
    workspaceId: project.workspace_id,
    name: project.name,
    receiverName: project.receiver_name,
    receiverEmail: project.receiver_email,
    status: project.status,
    startDate: project.start_date,
    updatedAt: project.updated_at,
    description: project.description || '',
    assigneeVisibility: project.assignee_visibility || 'HIDDEN',
    onboardingQuestions: [],
    milestones: (project.milestones || []).map((m: any) => ({
      id: m.id,
      title: m.title,
      description: m.description,
      status: m.status,
      duration: m.duration,
      completedAt: m.completed_at,
      assigneeId: m.assignee_id,
      comments: (m.comments || []).map((c: any) => ({
        id: c.id,
        author: c.author,
        role: c.role,
        text: c.text,
        date: c.date,
        isResolved: c.is_resolved,
        isDelivery: c.is_delivery,
      })),
    })),
    notes: (project.notes || []).map((n: any) => ({
      id: n.id,
      title: n.title,
      content: n.content,
      createdAt: n.created_at,
      updatedAt: n.updated_at,
      author: n.author,
      authorRole: n.author_role,
    })),
    files: [],
  }));
}

/**
 * Create a new project for the authenticated user
 */
export async function createProject(
  userId: string,
  workspaceId: string,
  projectData: {
    name: string;
    receiverName: string;
    receiverEmail: string;
    description?: string;
    status: string;
    startDate?: string;
    assigneeVisibility?: string;
  }
): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: userId,
      workspace_id: workspaceId,
      name: projectData.name,
      receiver_name: projectData.receiverName,
      receiver_email: projectData.receiverEmail,
      description: projectData.description || '',
      status: projectData.status,
      start_date: projectData.startDate || new Date().toISOString(),
      assignee_visibility: projectData.assigneeVisibility || 'HIDDEN',
    })
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating project:', error);
    return null;
  }

  // Return project in app schema format
  return {
    id: data.id,
    workspaceId: data.workspace_id,
    name: data.name,
    receiverName: data.receiver_name,
    receiverEmail: data.receiver_email,
    status: data.status,
    startDate: data.start_date,
    updatedAt: data.updated_at,
    description: data.description || '',
    assigneeVisibility: data.assignee_visibility || 'HIDDEN',
    onboardingQuestions: [],
    milestones: [],
    notes: [],
    files: [],
  };
}

/**
 * Create a new project with onboarding questions
 */
export async function createProjectWithQuestions(
  userId: string,
  workspaceId: string,
  projectData: {
    name: string;
    receiverName: string;
    receiverEmail: string;
    description?: string;
    status: string;
    startDate?: string;
    assigneeVisibility?: string;
    onboardingQuestions?: OnboardingQuestion[];
  }
): Promise<Project | null> {
  // Create the project first
  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: userId,
      workspace_id: workspaceId,
      name: projectData.name,
      receiver_name: projectData.receiverName,
      receiver_email: projectData.receiverEmail,
      description: projectData.description || '',
      status: projectData.status,
      start_date: projectData.startDate || new Date().toISOString(),
      assignee_visibility: projectData.assigneeVisibility || 'HIDDEN',
    })
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating project:', error);
    return null;
  }

  const projectId = data.id;

  // Save onboarding questions if provided
  if (projectData.onboardingQuestions && projectData.onboardingQuestions.length > 0) {
    const questionsToInsert = projectData.onboardingQuestions.map(q => ({
      project_id: projectId,
      text: q.text,
      required: q.required || false,
    }));

    const { error: questionsError } = await supabase
      .from('onboarding_questions')
      .insert(questionsToInsert);

    if (questionsError) {
      console.error('Error creating onboarding questions:', questionsError);
      // Don't fail the whole operation, but log the error
    }
  }

  // Return project in app schema format
  return {
    id: data.id,
    workspaceId: data.workspace_id,
    name: data.name,
    receiverName: data.receiver_name,
    receiverEmail: data.receiver_email,
    status: data.status,
    startDate: data.start_date,
    updatedAt: data.updated_at,
    description: data.description || '',
    assigneeVisibility: data.assignee_visibility || 'HIDDEN',
    onboardingQuestions: projectData.onboardingQuestions || [],
    milestones: [],
    notes: [],
    files: [],
  };
}

export async function deleteWorkspace(workspaceId: string): Promise<void> {
  try {
    const { data: projectRows } = await supabase
      .from('projects')
      .select('id')
      .eq('workspace_id', workspaceId);

    const projectIds = projectRows?.map((p: any) => p.id) || [];

    if (projectIds.length > 0) {
      const { data: milestoneRows } = await supabase
        .from('milestones')
        .select('id')
        .in('project_id', projectIds);

      const milestoneIds = milestoneRows?.map((m: any) => m.id) || [];

      if (milestoneIds.length > 0) {
        await supabase.from('comments').delete().in('milestone_id', milestoneIds);
      }

      await supabase.from('attachments').delete().in('project_id', projectIds);
      await supabase.from('notes').delete().in('project_id', projectIds);
      await supabase.from('onboarding_questions').delete().in('project_id', projectIds);
      await supabase.from('milestones').delete().in('project_id', projectIds);
      await supabase.from('projects').delete().eq('workspace_id', workspaceId);
    }

    await supabase.from('workspace_members').delete().eq('workspace_id', workspaceId);
    const { error } = await supabase.from('workspaces').delete().eq('id', workspaceId);
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting workspace:', error);
    throw error;
  }
}

/**
 * Check if a workspace name conflicts with existing workspaces for the user across roles
 * Returns error message if conflict exists, null if valid
 */
export async function validateWorkspaceName(
  userId: string,
  proposedName: string,
  targetRole: UserRole
): Promise<string | null> {
  const allWorkspaces = await fetchUserWorkspaces(userId);

  // Find workspace with the same name in the opposite role
  const oppositeRole = targetRole === UserRole.PROVIDER ? UserRole.RECEIVER : UserRole.PROVIDER;
  const conflictingWorkspace = allWorkspaces.find(
    ws => ws.name === proposedName && ws.role === oppositeRole
  );

  if (conflictingWorkspace) {
    const roleLabel = oppositeRole === UserRole.PROVIDER ? 'Provider' : 'Receiver';
    return `You already have a ${roleLabel} workspace named '${proposedName}'. Please choose a different name for your ${targetRole === UserRole.PROVIDER ? 'Provider' : 'Receiver'} workspace.`;
  }

  return null;
}

/**
 * Check if user has a workspace for the given role
 */
export async function hasWorkspaceForRole(
  userId: string,
  role: UserRole
): Promise<boolean> {
  const allWorkspaces = await fetchUserWorkspaces(userId);
  return allWorkspaces.some(ws => ws.role === role);
}
